import { FormEvent, useEffect, useState } from 'react';
import { KeyRound, Loader2, ShieldCheck, Trash2 } from 'lucide-react';
import { browserSupportsWebAuthn, startRegistration } from '@simplewebauthn/browser';
import { dtpApi, SignInMethods as Methods } from '../../lib/dtpApi';
import { loginErrorMessage } from '../../lib/patientLoginApi';
import { useDtpAuthStore } from '../../store/useDtpAuthStore';

const PASSWORD_MIN = 10;

/**
 * Password and passkeys for the top-level login (/patient/login). They belong to
 * the person, not this clinic record: one password or passkey opens every
 * clinic they're a patient of.
 */
export function SignInMethods() {
  const impersonated = useDtpAuthStore(s => s.impersonated);
  const [methods, setMethods] = useState<Methods | null>(null);
  const [password, setPassword] = useState('');
  const [editing, setEditing] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');

  useEffect(() => {
    if (impersonated) return;
    dtpApi.getSignInMethods().then(setMethods).catch(() => setError('We couldn’t load your sign-in methods.'));
  }, [impersonated]);

  // The server refuses these for a clinic viewing the portal; don't offer them.
  if (impersonated) return null;

  const act = async (action: () => Promise<void>, done: string) => {
    setBusy(true); setError(''); setNotice('');
    try { await action(); setNotice(done); setMethods(await dtpApi.getSignInMethods()); }
    catch (err) { setError(loginErrorMessage(err)); }
    finally { setBusy(false); }
  };

  const savePassword = (event: FormEvent) => {
    event.preventDefault();
    if (password.length < PASSWORD_MIN) { setError(`Use at least ${PASSWORD_MIN} characters.`); return; }
    act(async () => { await dtpApi.setPassword(password); setPassword(''); setEditing(false); }, 'Password saved.');
  };

  const addPasskey = () => act(async () => {
    const { challengeId, options } = await dtpApi.passkeyRegistrationOptions();
    const response = await startRegistration({ optionsJSON: options });
    await dtpApi.registerPasskey(challengeId, response, navigator.platform || undefined);
  }, 'Passkey added.');

  return <section aria-labelledby="sign-in-methods" className="portal-card mt-5 p-6 sm:p-8">
    <h2 id="sign-in-methods" className="portal-display flex items-center gap-3 text-xl font-semibold"><span aria-hidden="true" className="grid h-9 w-9 place-items-center rounded-xl bg-[var(--portal-tint-1)]"><ShieldCheck className="h-[18px] w-[18px]" /></span>Sign-in methods</h2>
    <p className="mt-2 text-sm text-[var(--portal-muted)]">Sign in at the patient login with a password or passkey instead of waiting for a code. They work for every clinic you’re a patient of.</p>

    {methods?.canManage === false ? (
      <p className="mt-4 rounded-lg bg-white/70 p-3 text-sm text-[var(--portal-ink)]">
        To add or change a password or passkey, sign in with a code texted to your phone. This keeps anyone with access to your email from changing how you sign in.
      </p>
    ) : !methods ? <p role="status" className="mt-4 text-sm text-[var(--portal-muted)]">{error || 'Loading…'}</p> : <div className="mt-5 space-y-6">
      <div>
        <p className="text-sm font-medium text-[var(--portal-ink)]">Password</p>
        {editing || !methods.hasPassword ? (
          <form onSubmit={savePassword} className="mt-2 flex flex-col gap-2 sm:flex-row">
            <label className="sr-only" htmlFor="new-password">New password</label>
            <input id="new-password" type="password" autoComplete="new-password" value={password} onChange={e => setPassword(e.target.value)}
              placeholder={`At least ${PASSWORD_MIN} characters`} className="h-12 flex-1 rounded-2xl border border-[rgba(36,26,21,0.12)] bg-white/90 px-4 focus:!border-[rgba(224,86,58,0.5)] focus:outline-none focus:ring-4 focus:!ring-[rgba(224,86,58,0.15)]" />
            <button disabled={busy} className="h-12 rounded-full bg-[var(--portal-primary,#241a15)] px-6 text-sm font-semibold text-[var(--portal-primary-fg,#fff6ef)] disabled:opacity-50">
              {methods.hasPassword ? 'Change password' : 'Set password'}
            </button>
          </form>
        ) : (
          <div className="mt-1 flex items-center gap-4 text-sm">
            <span className="text-[var(--portal-muted)]">Set{methods.passwordSetAt ? ` ${new Date(methods.passwordSetAt).toLocaleDateString()}` : ''}</span>
            <button onClick={() => setEditing(true)} className="font-medium text-[var(--portal-ink)] underline">Change</button>
            <button onClick={() => act(() => dtpApi.removePassword(), 'Password removed.')} disabled={busy} className="text-[var(--portal-muted)] underline">Remove</button>
          </div>
        )}
      </div>

      {browserSupportsWebAuthn() && <div>
        <p className="text-sm font-medium text-[var(--portal-ink)]">Passkeys</p>
        {methods.passkeys.length > 0 && <ul className="mt-2 divide-y divide-[rgba(36,26,21,0.06)] rounded-2xl border border-[rgba(36,26,21,0.08)] bg-white/70">
          {methods.passkeys.map(pk => <li key={pk.id} className="flex items-center justify-between px-3 py-2 text-sm">
            <span><KeyRound className="mr-2 inline h-4 w-4 text-[var(--portal-muted)]" />{pk.deviceName} <span className="text-[var(--portal-muted)]">· added {new Date(pk.createdAt).toLocaleDateString()}</span></span>
            <button aria-label={`Remove passkey ${pk.deviceName}`} onClick={() => act(() => dtpApi.removePasskey(pk.id), 'Passkey removed.')} disabled={busy}
              className="p-2 text-[var(--portal-muted)] hover:text-red-700"><Trash2 className="h-4 w-4" /></button>
          </li>)}
        </ul>}
        <button onClick={addPasskey} disabled={busy}
          className="mt-2 flex h-11 items-center gap-2 rounded-full border border-[rgba(36,26,21,0.12)] bg-white/80 px-5 text-sm font-semibold hover:bg-white disabled:opacity-50">
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <KeyRound className="h-4 w-4" />} Add a passkey
        </button>
      </div>}

      {error && <p role="alert" className="rounded-lg bg-red-50 p-3 text-sm text-red-700">{error}</p>}
      {notice && <p role="status" className="text-sm text-green-700">{notice}</p>}
    </div>}
  </section>;
}
